import { SparklesIcon } from "@heroicons/react/24/outline"
import type { RowSelectionState } from "@tanstack/react-table"
import { useCallback, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import { useTriggerAnalyzeCreativeMutation } from "~/ui-rtk/api/jobsApi"
import { toCreativeAnalysisPlatform } from "~/ui-rtk/api/paidMediaApi"
import { Dialog } from "~/ui-rtk/components/ui/common"
import { Button } from "~/ui-rtk/components/ui/controls"
import CheckboxWithLabel from "~/ui-rtk/components/ui/controls/CheckboxWithLabel"
import { H3, P } from "~/ui-rtk/components/ui/typography"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import type { TMetric } from "~/ui-rtk/constants/table-visualization-types"

/**
 * "Analyze with AI" for the Paid Media Optimization Ads
 * table. With rows ticked, exactly those ads are queued; otherwise every
 * META ad currently rendered (the paginated page after filters and sort) is,
 * in table order — into the durable `analyze-creative` job, one ad per step,
 * results saved onto each ad's asset row and visible on the Jobs page. Rows
 * from other channels (TikTok, YouTube, Google) are left out on purpose.
 *
 * The confirm dialog offers two switches:
 * - "skip already analyzed" (on by default, MD-5612): an ad with a saved
 *   analysis is not sent through Gemini again; its stored suggestions are
 *   reused, so re-running the page costs nothing for ads already done.
 * - "auto-apply suggested tags": off, the report's tag picks stay
 *   suggestions a human applies from the drawer; on, the job writes them as
 *   tag assignments (source ai_suggested) — never over a human's pick.
 *
 * Open to every user with paid-media access. Each video is a paid Gemini
 * pass and a page can be 100 ads, which is why the confirm dialog exists.
 */

/** Server-side cap on adIds per analyze-creative run. */
const MAX_ADS_PER_JOB = 100

const DIALOG_CONTAINER_CLS =
  "rounded-lg border border-basic-blue bg-basic-dark-blue text-left text-white shadow-xl"

const isMetaRow = (row: TMetric) =>
  toCreativeAnalysisPlatform(row.media_channel as string | undefined) === "meta"

/**
 * The Meta ad ids on the page, in table order. Other channels are skipped;
 * duplicates (the same ad across placements) are analyzed once.
 */
export function metaAdIdsOnPage(rows: TMetric[]): string[] {
  const seen = new Set<string>()
  const adIds: string[] = []
  for (const row of rows) {
    const adId = String(row.ad_id ?? "")
    if (!adId || !isMetaRow(row) || seen.has(adId)) continue
    seen.add(adId)
    adIds.push(adId)
  }
  return adIds
}

/**
 * The Meta ad ids behind the ticked rows. TanStack keys the selection by row
 * index into the rendered page, which is exactly the list `onRowsRendered`
 * reported — so an index maps straight onto `rows`.
 */
export function selectedMetaAdIds(
  rows: TMetric[],
  rowSelection: RowSelectionState,
): string[] {
  const picked = Object.keys(rowSelection)
    .filter(key => rowSelection[key])
    .map(key => rows[Number(key)])
    .filter((row): row is TMetric => !!row)
  return metaAdIdsOnPage(picked)
}

const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size))
  }
  return out
}

/**
 * Keeps the rows the table last reported, ignoring re-reports of the same
 * page (the table may call back with a fresh array holding the same rows on
 * every render — comparing the id list keeps this from re-rendering the
 * dashboard in a loop).
 */
export function useVisibleRows(): [TMetric[], (rows: TMetric[]) => void] {
  const [rows, setRows] = useState<TMetric[]>([])
  const keyRef = useRef<string>("")
  const onRowsRendered = useCallback((next: TMetric[]) => {
    const key = next.map(row => String(row.ad_id ?? "")).join(" ")
    if (key === keyRef.current) return
    keyRef.current = key
    setRows(next)
  }, [])
  return [rows, onRowsRendered]
}

interface AnalyzeVisibleAdsButtonProps {
  rows: TMetric[]
  /** Ad ids behind the rows the user ticked; when non-empty, only these are queued. */
  selectedAdIds?: string[]
}

export default function AnalyzeVisibleAdsButton({
  rows,
  selectedAdIds = [],
}: AnalyzeVisibleAdsButtonProps) {
  const [triggerAnalyzeCreative] = useTriggerAnalyzeCreativeMutation()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isQueueing, setIsQueueing] = useState(false)
  const [autoApplyTags, setAutoApplyTags] = useState(false)
  const [skipAnalyzed, setSkipAnalyzed] = useState(true)

  const pageAdIds = useMemo(() => metaAdIdsOnPage(rows), [rows])
  const isSelection = selectedAdIds.length > 0
  const metaAdIds = isSelection ? selectedAdIds : pageAdIds
  const total = metaAdIds.length
  const skipped = useMemo(
    () =>
      isSelection
        ? 0
        : new Set(rows.map(row => String(row.ad_id ?? ""))).size -
          pageAdIds.length,
    [isSelection, rows, pageAdIds.length],
  )
  const noun = isSelection ? "selected ad" : "Meta ad"
  const subject = `${total} ${noun}${total === 1 ? "" : "s"}`

  const handleConfirm = async () => {
    if (isQueueing || total === 0) return
    setIsQueueing(true)
    try {
      const jobRunIds: string[] = []
      for (const adIds of chunk(metaAdIds, MAX_ADS_PER_JOB)) {
        const { jobRunId } = await triggerAnalyzeCreative({
          adIds,
          platform: "meta",
          save: true,
          autoApplyTags,
          skipAnalyzed,
        }).unwrap()
        jobRunIds.push(jobRunId)
      }
      toast.success(
        <span>
          Queued {subject} for AI analysis ({jobRunIds.length} job
          {jobRunIds.length === 1 ? "" : "s"}
          {autoApplyTags ? ", tags auto-applied" : ""}).{" "}
          <Link to={brandRoutes.JOBS.ROOT} className="underline">
            Follow progress on the Jobs page
          </Link>
          .
        </span>,
      )
      setIsConfirmOpen(false)
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message
      toast.error(message ?? "Could not queue the analysis")
    } finally {
      setIsQueueing(false)
    }
  }

  if (total === 0) return null

  return (
    <>
      <Button
        variant={{ variant: "outlined" }}
        size="sm"
        type="button"
        className="h-system-2xl"
        onClick={() => setIsConfirmOpen(true)}
        title={
          isSelection
            ? "Analyze the ticked ads with Marathon Vision"
            : "Analyze every Meta ad on this page with Marathon Vision (tick rows to pick specific ads)"
        }
      >
        <SparklesIcon className="w-4 h-4 mr-1" />
        Analyze {subject} with AI
      </Button>

      <Dialog
        isOpen={isConfirmOpen}
        handleClose={() => {
          if (!isQueueing) setIsConfirmOpen(false)
        }}
        width={460}
        classes={{ container: DIALOG_CONTAINER_CLS }}
      >
        <div className="flex flex-col gap-3 p-system-md">
          <H3 className="text-white font-bold text-lg">
            Analyze {subject} with AI?
          </H3>
          <P className="text-sm text-system-gray-300">
            {isSelection
              ? "The ticked ads are queued"
              : "Every Meta ad on this page is queued in table order"}{" "}
            into a background job: Marathon Vision reads each creative (videos
            take ~30-90s each) and saves the report and tag suggestions onto the
            ad. Each video is a paid Marathon Vision pass.
          </P>
          {skipped > 0 && (
            <P className="text-xs text-system-gray-500">
              {skipped} non-Meta ad{skipped === 1 ? "" : "s"} on this page
              {skipped === 1 ? " is" : " are"} left out.
            </P>
          )}
          <CheckboxWithLabel
            checked={skipAnalyzed}
            onChange={e => setSkipAnalyzed(e.target.checked)}
            disabled={isQueueing}
            className="checkbox checkbox-sm"
          >
            <span className="text-sm text-white">
              Skip ads that already have an analysis
            </span>
          </CheckboxWithLabel>
          <P className="text-xs text-system-gray-500 -mt-2 ml-7">
            {skipAnalyzed
              ? "An ad analyzed before is not sent to Marathon Vision again; its saved suggestions are reused."
              : "Every ad is re-analyzed, including ones with a saved report."}
          </P>
          <CheckboxWithLabel
            checked={autoApplyTags}
            onChange={e => setAutoApplyTags(e.target.checked)}
            disabled={isQueueing}
            className="checkbox checkbox-sm"
          >
            <span className="text-sm text-white">
              Auto-apply suggested tags
            </span>
          </CheckboxWithLabel>
          <P className="text-xs text-system-gray-500 -mt-2 ml-7">
            {autoApplyTags
              ? "Suggested tags are added as tag assignments as each ad finishes. Categories a person already tagged are left as they are."
              : "Tags stay suggestions; apply them per ad from the ad drawer."}
          </P>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant={{ variant: "outlined" }}
              size="sm"
              type="button"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isQueueing}
            >
              Cancel
            </Button>
            <Button
              variant={{ variant: "solid", color: "pink" }}
              size="sm"
              type="button"
              isLoading={isQueueing}
              disabled={isQueueing}
              onClick={() => void handleConfirm()}
            >
              Queue analysis
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}
